import { API_KEY_LAUNCH_SCOPES, type ApiKeyScope } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, EmptyState, Icon, TableSkeletonRows } from "@sagot/ui";
import { useState } from "react";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { useFormatters, wsKeys, type ApiKeyOut } from "../../shared";
import { AllowedRanges } from "../panels/api-keys-panel";
import { CreateKeyDialog } from "../panels/create-key-dialog";
import { KeyAllowlistDialog } from "../panels/key-allowlist-dialog";
import { Gate, PanelError, TableScroll, useCanManage } from "../panels/panel-kit";
import { RetireBadge, RotateKeyDialog } from "../panels/rotate-key-dialog";
import { useApiKeys, useInvalidate } from "../use-settings";

const COLUMNS = 6;
const LAUNCH_SCOPES: readonly string[] = API_KEY_LAUNCH_SCOPES;
const STARTER_SCOPES: readonly ApiKeyScope[] = ["launch:write", "customers:write"];
const K = "launch.keys";

/** Active keys that can start a launch or write customer context. */
export function launchKeys(keys: readonly ApiKeyOut[]): ApiKeyOut[] {
  return keys.filter((key) => key.active && key.scopes.some((scope) => LAUNCH_SCOPES.includes(scope)));
}

type Editing = { kind: "security" | "rotate"; id: string } | null;

function LaunchKeyRow({ apiKey, manage, onSecurity, onRotate }: { apiKey: ApiKeyOut; manage: ReturnType<typeof useCanManage>; onSecurity: () => void; onRotate: () => void }) {
  const { t } = useTranslation();
  const format = useFormatters();
  return (
    <tr data-testid="launch-key-row">
      <td>
        <b>{apiKey.name}</b>
        <div className="font-mono text-xs text-fg-muted">{apiKey.display}</div>
        {apiKey.signing_configured ? (
          <Badge tone={apiKey.signing_required ? "success" : "brand"} className="mt-1">
            <Icon name="shield-check" className="size-3" />
            {t(`${K}.signed`)}
          </Badge>
        ) : null}
      </td>
      <td>
        <span className="flex flex-wrap gap-1">
          {apiKey.scopes.map((scope) => (
            <Badge key={scope} tone={LAUNCH_SCOPES.includes(scope) ? "brand" : "neutral"} className="font-mono">
              {scope}
            </Badge>
          ))}
        </span>
      </td>
      <td>
        <AllowedRanges ranges={apiKey.allowed_ip_ranges} />
      </td>
      <td className="whitespace-nowrap text-fg-muted">{apiKey.last_used_at ? format.relative(apiKey.last_used_at) : t(`${K}.neverUsed`)}</td>
      <td>
        {apiKey.rotation ? <RetireBadge until={apiKey.rotation.previous_valid_until} label={(time) => t(`${K}.retires`, { time })} /> : apiKey.expires_at ? format.date(apiKey.expires_at) : <span className="text-fg-muted">{t(`${K}.neverExpires`)}</span>}
      </td>
      <td className="text-right">
        <Gate reason={manage.reason}>
          <span className="inline-flex flex-col items-end gap-1">
            <Button variant="ghost" size="sm" disabled={!manage.allowed} onClick={onSecurity} aria-label={t(`${K}.securityOne`, { name: apiKey.name })}>
              {t(`${K}.security`)}
            </Button>
            <Button variant="secondary" size="sm" disabled={!manage.allowed} onClick={onRotate} aria-label={t(`${K}.rotateOne`, { name: apiKey.name })}>
              <Icon name="refresh-cw" />
              {t(`${K}.rotate`)}
            </Button>
          </span>
        </Gate>
      </td>
    </tr>
  );
}

/**
 * Secure launch "Server key" card (mockup secure-launch pin 5): the scoped key the operator's server
 * uses, its IP allowlist, signing state and the rotate action with the 24 h overlap. Self-contained:
 * the Secure launch page only has to render it.
 */
export function ScopedKeyCard() {
  const { t } = useTranslation();
  const wsId = useWorkspaceId();
  const invalidate = useInvalidate();
  const keys = useApiKeys();
  const manage = useCanManage("api_keys.manage");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const refresh = () => invalidate(wsKeys.apiKeys(wsId));
  if (keys.isError) return <PanelError error={keys.error} onRetry={() => void keys.refetch()} />;
  const rows = launchKeys(keys.data ?? []);
  const editedKey = editing ? rows.find((candidate) => candidate.id === editing.id) : undefined;
  const createButton = (
    <Gate reason={manage.reason}>
      <Button size="sm" variant={rows.length === 0 ? "primary" : "secondary"} disabled={!manage.allowed} onClick={() => { setCreating(true); }}>
        <Icon name="plus" />
        {t(`${K}.create`)}
      </Button>
    </Gate>
  );
  return (
    <>
      <section className="card overflow-hidden" aria-labelledby="scoped-key-title" data-testid="scoped-key-card">
        <div className="card-header">
          <div>
            <h2 id="scoped-key-title" className="card-title">
              {t(`${K}.title`)}
            </h2>
            <p className="mt-0.5 text-xs text-fg-subtle">{t(`${K}.subtitle`)}</p>
          </div>
          {createButton}
        </div>
        {!keys.isPending && rows.length === 0 ? (
          <EmptyState icon="key" headingLevel={3} title={t(`${K}.emptyTitle`)} description={t(`${K}.emptyBody`)} action={createButton} />
        ) : (
          <>
            <TableScroll label={t(`${K}.title`)}>
              <table className="table" data-testid="scoped-keys">
                <thead>
                  <tr>
                    <th>{t(`${K}.colName`)}</th>
                    <th>{t(`${K}.colScopes`)}</th>
                    <th>{t(`${K}.colAllowed`)}</th>
                    <th>{t(`${K}.colLastUsed`)}</th>
                    <th>{t(`${K}.colExpires`)}</th>
                    <th>
                      <span className="sr-only">{t(`${K}.colActions`)}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {keys.isPending ? (
                    <TableSkeletonRows rows={2} columns={COLUMNS} />
                  ) : (
                    rows.map((apiKey) => (
                      <LaunchKeyRow
                        key={apiKey.id}
                        apiKey={apiKey}
                        manage={manage}
                        onSecurity={() => { setEditing({ kind: "security", id: apiKey.id }); }}
                        onRotate={() => { setEditing({ kind: "rotate", id: apiKey.id }); }}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </TableScroll>
            <p className="border-t border-line px-5 py-3 text-xs text-fg-muted">{t(`${K}.note`)}</p>
          </>
        )}
      </section>
      {creating ? <CreateKeyDialog initialScopes={STARTER_SCOPES} initialName={t(`${K}.createDefaultName`)} onClose={() => { setCreating(false); }} onCreated={refresh} /> : null}
      {editedKey && editing?.kind === "security" ? <KeyAllowlistDialog key={editedKey.id} apiKey={editedKey} onClose={() => { setEditing(null); }} onChanged={refresh} /> : null}
      {editedKey && editing?.kind === "rotate" ? <RotateKeyDialog key={editedKey.id} apiKey={editedKey} onClose={() => { setEditing(null); }} onRotated={refresh} /> : null}
    </>
  );
}
